import { createContext, useContext, useState } from "react";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { IReportsFilesRepository } from "domain/repositories/reports-files-repository";
import { GlobalAuthContext } from "contexts/auth";
import { createBlobDownloader } from "utils/files/downloadBlob";

type TReportsFilesProviderProps = {
  children: React.ReactNode;
  reportsFilesDatasource: IReportsFilesRepository;
};

export type TRerportsFilesState = {
  downloadingReports?: boolean;
  downloadPdfReport: () => void;
};

const initContext: TRerportsFilesState = {
  downloadPdfReport: () => null,
};
export const ReportsFilesContext = createContext(initContext);

export const ReportsFilesProvider = ({
  children,
  reportsFilesDatasource,
}: TReportsFilesProviderProps) => {
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { filterState, comparisonEnabled } = useContext(ReportsFilterContext);
  const { token } = useContext(GlobalAuthContext);

  const getBaseUrl = (): string => {
    const fullUrl = window.location.href;
    const baseUrl = fullUrl.split("?")[0];
    return baseUrl;
  };

  const getKeyWithSuffix = (key: string, index: number): string => {
    return index === 1 ? `${key}2` : key;
  };

  const getExperienceParameter = (index: number): string | undefined => {
    const parameters = [
      {
        key: getKeyWithSuffix("urlId", index),
        value: filterState[index]?.url?.value,
      },
      {
        key: getKeyWithSuffix("creativeId", index),
        value: filterState[index]?.creative?.value,
      },
      {
        key: getKeyWithSuffix("campaignId", index),
        value: filterState[index]?.campaign?.value,
      },
      {
        key: getKeyWithSuffix("portfolioId", index),
        value: filterState[index]?.portfolio?.value,
      },
    ];

    const param = parameters.find(({ value }) => value !== undefined);

    return param ? `${param.key}=${param.value}` : undefined;
  };

  const getRangeParameter = (index: number): string | undefined => {
    const state = filterState[index];
    if (state?.startDate && state?.endDate) {
      return `${getKeyWithSuffix("startDate", index)}=${
        state.startDate
      }&&${getKeyWithSuffix("endDate", index)}=${state.endDate}`;
    }

    return `${getKeyWithSuffix("rangeId", index)}=${state?.ranges?.value}`;
  };

  const getDeviceParameter = (index: number): string | undefined => {
    return `${getKeyWithSuffix("deviceId", index)}=${
      filterState[index]?.devices?.value
    }`;
  };

  const getTokenParameter = (): string => {
    return `token=${token}`;
  };

  const getComparisonParameter = (): string => {
    return `comparisonEnabled=${comparisonEnabled ?? false}`;
  };

  const getDownloadReportUrl = (): string => {
    const baseUrl = getBaseUrl();

    const params = [];
    if (comparisonEnabled) {
      for (let i = 0; i < 2; i++) {
        params.push(
          getExperienceParameter(i),
          getRangeParameter(i),
          getDeviceParameter(i)
        );
      }
    } else {
      params.push(
        getExperienceParameter(0),
        getRangeParameter(0),
        getDeviceParameter(0)
      );
    }

    params.push(getComparisonParameter());
    params.push(getTokenParameter());

    return `${baseUrl}?${params
      .filter((param) => param !== undefined)
      .join("&")}`;
  };

  const getFileNameFromUrl = (fileUrl: string) => {
    const url = new URL(fileUrl);
    const pathname = url.pathname;
    return pathname.split("/").pop()?.split("?")[0] || "download.pdf";
  };

  const downloadFile = async (fileUrl: string) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();
      const fileName = getFileNameFromUrl(fileUrl);

      createBlobDownloader(fileName)(blob);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const downloadPdfReport = () => {
    setDownloadingReports(true);
    const downloadReportUrl = getDownloadReportUrl();

    return reportsFilesDatasource
      .generatePdf(downloadReportUrl)
      .then((fileUrl: string) => {
        downloadFile(fileUrl);
      })
      .finally(() => setDownloadingReports(false));
  };

  return (
    <ReportsFilesContext.Provider
      value={{
        downloadingReports,
        downloadPdfReport,
      }}
    >
      {children}
    </ReportsFilesContext.Provider>
  );
};
